<template>
  <app-select
    ref="selectRef"
    v-bind="$attrs"
    :active="searchText?.length ? true : false"
    :items="computedItems"
    :item-text="itemText"
    :item-value="itemValue"
    :disabled="disabled"
    :clearClick="clearClick"
    :optionsTabDisabed="true"
    class="app-autocomplete"
    v-model="computedModelValue"
    @change="onChange"
  >
    <template #header>
      <input
        type="text"
        v-model="searchText"
        :disabled="disabled"
        :inputmode="onlyNumbers ? 'numeric' : 'text'"
        class="app__autocomplete__input"
        @focus="selectRef?.activateOptions()"
        @keyup.enter="onEnter"
        @keyup="debouncedWatch"
        @input="onInput"
      />
    </template>
    <template #item="item">
      <slot name="item" :item="item" />
    </template>
  </app-select>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, watch, onMounted, nextTick, onBeforeUnmount } from 'vue'
import AppSelect from '../AppSelect/AppSelect.vue'
import debounce from "lodash/debounce";

const props = defineProps({
  modelValue: { type: null, default: null },
  items: { type: Array, default: () => [] },
  itemText: { type: String, default: 'label' },
  itemValue: { type: String, default: 'value' },
  disabled: { type: Boolean, default: false },
  clearClick: { type: Boolean, default: false },
  onlyNumbers: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'invalid', 'change'])

const searchText = ref('')
const selectRef = ref<InstanceType<typeof AppSelect> | null>(null)

const computedModelValue = computed({
  get: () => props.modelValue,
  set: (newValue) => emit('update:modelValue', newValue),
})

const computedItems = computed(() => props.items)

const updateSearchText = (value: any) => {
  if (value && props.items.length) {
    const item = props.items.find(
        (item) => (item as Record<string, any>)[props.itemValue] === value
    )
    searchText.value = item ? (item as Record<string, any>)[props.itemText] : ''
  }
}

function clearSearch() {
  searchText.value = ''
}

function onEnter() {
  if(searchText.value) {
    const foundItem = computedItems.value.find(item => item[props.itemText] === searchText.value)
    if(foundItem) {
      computedModelValue.value = foundItem[props.itemValue]
      selectRef.value?.deactivateOptions()
    }
  }
}

function onInput(event: InputEvent) {
  if (!event.target) return;

  if(props.onlyNumbers) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9.]/g, ''); // Разрешаем только цифры и точку

    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    input.value = value;
    searchText.value = value;

    return;
  }

  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^0-9]/g, '');

  input.value = value;
  searchText.value = value;
}

function checkInputValidity() {
  if (searchText.value === '') {
    emit('invalid', false);
    return null;
  }
  const item = props.items?.find(item => item[props.itemText] === searchText.value);
  emit('invalid', !item);

  return item;
}

function checkValueValidity() {
  if (computedModelValue.value === '') {
    emit('invalid', false);
    return null;
  }
  const item = props.items?.find(item => item[props.itemValue] === computedModelValue.value);
  emit('invalid', !item);

  return item;
}

defineExpose({ clearSearch })

watchEffect(() => updateSearchText(computedModelValue.value))

onMounted(() => updateSearchText(computedModelValue.value))

onMounted(() => {
  if(computedModelValue.value !== undefined && computedModelValue.value !== null) {
    const item = checkValueValidity();
    searchText.value = item ? item[props.itemText] : searchText.value;
  }

  nextTick(() => {
    watch(
        [() => computedModelValue.value, () => props.items],
        () => {
          if (computedModelValue.value && props.items.length) {
            const item = checkValueValidity();

            if(item) searchText.value = item[props.itemText]
            else {
              searchText.value = computedModelValue.value
            }
          }
        }
    )
  })
})

const debouncedWatch = debounce(() => {
  const item = checkInputValidity();

  if (item) {
    computedModelValue.value = item[props.itemValue];
  }
  else {
    computedModelValue.value = searchText.value;
  }
}, 500); // 500 мс задержка

onBeforeUnmount(() => {
  debouncedWatch.cancel()
})

watch(computedModelValue, (newValue) => {
  if (newValue === null) searchText.value = ''
})

const onChange = () => {
  emit('invalid', false)
  emit('change')
}
</script>


<style lang="scss" src="./AppAutocomplete.scss"></style>
